import { useEffect, useRef } from 'react';
import { Transcript } from '../api/client';

interface TranscriptDetailProps {
  transcript: Transcript;
  onClose: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LeafletMap = any;

export function TranscriptDetail({ transcript, onClose }: TranscriptDetailProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<LeafletMap | null>(null);

  useEffect(() => {
    // Only initialize map if we have GPS coordinates
    if (
      !mapRef.current ||
      transcript.gps_lat === null ||
      transcript.gps_lon === null ||
      typeof window.L === 'undefined'
    ) {
      return;
    }

    // Create map
    const map = window.L.map(mapRef.current).setView(
      [transcript.gps_lat, transcript.gps_lon],
      15
    );

    // Add OpenStreetMap tiles
    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    // Add marker
    window.L.marker([transcript.gps_lat, transcript.gps_lon]).addTo(map);

    // Add accuracy circle if available
    if (transcript.gps_accuracy) {
      window.L.circle([transcript.gps_lat, transcript.gps_lon], {
        radius: transcript.gps_accuracy,
        color: '#1976d2',
        fillColor: '#1976d2',
        fillOpacity: 0.1,
      }).addTo(map);
    }

    mapInstanceRef.current = map;

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [transcript.gps_lat, transcript.gps_lon, transcript.gps_accuracy]);

  const formatDate = (ts: number) => {
    return new Date(ts).toLocaleString();
  };

  return (
    <div className="transcript-detail-overlay" onClick={onClose}>
      <div className="transcript-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="transcript-detail-header">
          <h2>Transcript Details</h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="transcript-detail-content">
          {/* Text Content */}
          <div className="detail-section">
            <h3>Transcript</h3>
            <p className="transcript-text-full">
              {transcript.enhanced_text || transcript.transcript_text}
            </p>
            {transcript.enhanced_text && (
              <details className="raw-text-toggle">
                <summary>Show original text</summary>
                <p className="raw-text">{transcript.transcript_text}</p>
              </details>
            )}
          </div>

          {/* Metadata */}
          <div className="detail-section">
            <h3>Metadata</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Timestamp</span>
                <span className="detail-value">{formatDate(transcript.timestamp)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">ID</span>
                <span className="detail-value detail-mono">{transcript.id}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Created</span>
                <span className="detail-value">{formatDate(transcript.created_at)}</span>
              </div>
              {transcript.enhanced_at && (
                <div className="detail-item">
                  <span className="detail-label">Enhanced</span>
                  <span className="detail-value">{formatDate(transcript.enhanced_at)}</span>
                </div>
              )}
              {transcript.enhancement_model && (
                <div className="detail-item">
                  <span className="detail-label">Model</span>
                  <span className="detail-value">{transcript.enhancement_model}</span>
                </div>
              )}
            </div>
          </div>

          {/* Location */}
          {transcript.gps_lat !== null && transcript.gps_lon !== null && (
            <div className="detail-section">
              <h3>Location</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Coordinates</span>
                  <span className="detail-value">
                    {transcript.gps_lat.toFixed(6)}, {transcript.gps_lon.toFixed(6)}
                  </span>
                </div>
                {transcript.gps_accuracy && (
                  <div className="detail-item">
                    <span className="detail-label">Accuracy</span>
                    <span className="detail-value">{transcript.gps_accuracy.toFixed(0)}m</span>
                  </div>
                )}
              </div>
              <div ref={mapRef} className="detail-map" />
              <a
                href={`https://maps.google.com/?q=${transcript.gps_lat},${transcript.gps_lon}`}
                target="_blank"
                rel="noopener noreferrer"
                className="map-link"
              >
                Open in Google Maps
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Leaflet type declaration for window
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    L: any;
  }
}
